import { useState, useRef } from "react";
import { Layout } from "@/components/layout/Layout";
import { HeroSection } from "@/components/home/HeroSection";
import { CategoryFilters } from "@/components/home/CategoryFilters";
import { ExperienceGrid } from "@/components/experiences/ExperienceGrid";
import { useExperiences } from "@/hooks/useExperiences";
import { SEOHead } from "@/components/seo/SEOHead";

const Index = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("All Vibes");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const experiencesSectionRef = useRef<HTMLElement>(null);

  const categoryParam = selectedCategory === "All Vibes" ? undefined : selectedCategory;
  const { data: experiences = [], isLoading, error } = useExperiences(categoryParam, activeSearch);

  const handleSearch = () => {
    setActiveSearch(searchQuery);
    // Scroll to experiences section when searching
    experiencesSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Layout>
      <SEOHead
        title="Discover Unique Experiences in Varkala"
        description="Book authentic experiences in Varkala - from yoga and surfing to cooking classes and adventure tours. Discover the best things to do in Varkala with local hosts."
        canonicalUrl="https://www.wanderdaddy.com/"
        keywords="things to do in Varkala, Varkala experiences, activities in Varkala, yoga in Varkala, surf lessons Varkala, cooking class Varkala, adventure tours Varkala"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "WanderDaddy",
          "url": "https://www.wanderdaddy.com",
          "description": "Discover and book unique experiences in Varkala",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.wanderdaddy.com/?search={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      <HeroSection
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearch={handleSearch}
      />
      <section ref={experiencesSectionRef} className="container py-12">
        <div className="mb-8">
          <h2 className="font-display text-3xl font-bold tracking-tight">
            Discover Experiences
          </h2>
          <p className="mt-2 text-muted-foreground">
            Find your perfect adventure in paradise
          </p>
        </div>
        <div className="mb-8">
          <CategoryFilters
            selected={selectedCategory}
            onSelect={setSelectedCategory}
          />
        </div>
        {error ? (
          <div className="py-12 text-center">
            <p className="text-lg text-destructive">
              Failed to load experiences. Please try again later.
            </p>
          </div>
        ) : (
          <ExperienceGrid experiences={experiences} isLoading={isLoading} />
        )}
      </section>
    </Layout>
  );
};

export default Index;
